import { useState, useEffect } from 'react';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface Props {
  mode: 'range' | 'single';
  startDate: string | null;
  endDate: string | null;
  singleDate: string | null;
  onStartChange: (iso: string | null) => void;
  onEndChange: (iso: string | null) => void;
  onSingleChange: (iso: string | null) => void;
  onClear: () => void;
}

const toDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toIso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const sameDay = (a: Date | null, b: Date | null) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const InlineCalendar = ({
  mode,
  startDate,
  endDate,
  singleDate,
  onStartChange,
  onEndChange,
  onSingleChange,
  onClear,
}: Props) => {
  const todayRaw = new Date();
  todayRaw.setHours(0, 0, 0, 0);

  const [viewYear, setViewYear] = useState(todayRaw.getFullYear());
  const [viewMonth, setViewMonth] = useState(todayRaw.getMonth());
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [awaitingEnd, setAwaitingEnd] = useState(false);

  useEffect(() => {
    if (mode === 'range' && startDate && !endDate) {
      setAwaitingEnd(true);
    } else {
      setAwaitingEnd(false);
    }
    setHoverDate(null);
  }, [mode, startDate, endDate]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  };

  const goToday = () => {
    setViewYear(todayRaw.getFullYear());
    setViewMonth(todayRaw.getMonth());
  };

  const handleDayClick = (dt: Date) => {
    if (mode === 'single') {
      onSingleChange(toIso(dt));
      return;
    }
    if (!awaitingEnd || (startDate && endDate)) {
      onStartChange(toIso(dt));
      onEndChange(null);
      setAwaitingEnd(true);
    } else {
      const start = toDate(startDate);
      if (start && dt < start) {
        onEndChange(toIso(start));
        onStartChange(toIso(dt));
      } else {
        onEndChange(toIso(dt));
      }
      setAwaitingEnd(false);
      setHoverDate(null);
    }
  };

  const handleDayHover = (dt: Date) => {
    if (mode === 'range' && awaitingEnd) setHoverDate(dt);
  };

  const handleDayLeave = () => {
    if (hoverDate) setHoverDate(null);
  };

  // Build grid cells
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

  const start = toDate(startDate);
  const end = toDate(endDate);
  const single = toDate(singleDate);
  const effEnd = end || (mode === 'range' && awaitingEnd ? hoverDate : null);
  const lo = start && effEnd ? (start <= effEnd ? start : effEnd) : null;
  const hi = start && effEnd ? (start <= effEnd ? effEnd : start) : null;

  type Cell = { d: number; current: boolean; dt: Date };
  const cells: Cell[] = [];

  for (let i = 0; i < firstDayOfWeek; i++) {
    const d = daysInPrev - firstDayOfWeek + 1 + i;
    cells.push({ d, current: false, dt: new Date(viewYear, viewMonth - 1, d) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ d, current: true, dt: new Date(viewYear, viewMonth, d) });
  }
  const rem = cells.length % 7;
  if (rem > 0) {
    for (let i = 1; i <= 7 - rem; i++) {
      cells.push({ d: i, current: false, dt: new Date(viewYear, viewMonth + 1, i) });
    }
  }

  const weeks: Cell[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div className="dfc-root">
      <div className="dfc-nav">
        <button className="dfc-nav-btn" onClick={prevMonth}>
          &#8249;
        </button>
        <span className="dfc-month-label">
          {MONTHS[viewMonth]} {viewYear}
        </span>
        <button className="dfc-nav-btn" onClick={nextMonth}>
          &#8250;
        </button>
      </div>

      <table className="dfc-table">
        <thead>
          <tr>
            {DAYS_SHORT.map((d) => (
              <th key={d}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map(({ d, current, dt }, ci) => {
                let dayClass = current ? 'dfc-day' : 'dfc-day dfc-other';
                let tdClass = '';

                if (current && sameDay(dt, todayRaw)) dayClass += ' dfc-today';

                if (mode === 'single') {
                  if (current && sameDay(dt, single)) dayClass += ' dfc-selected';
                } else {
                  const isStart = current && sameDay(dt, start);
                  const isEnd =
                    current &&
                    (sameDay(dt, end) ||
                      (!end && hoverDate && awaitingEnd && sameDay(dt, hoverDate) && !!start));
                  const inRange = current && lo && hi && dt > lo && dt < hi;

                  if (isStart) {
                    dayClass += ' dfc-range-start';
                    tdClass = 'dfc-td-start';
                  }
                  if (isEnd) {
                    dayClass += ' dfc-range-end';
                    tdClass = isStart ? '' : 'dfc-td-end';
                  }
                  if (inRange) tdClass = 'dfc-td-in-range';
                }

                return (
                  <td key={ci} className={tdClass}>
                    <div
                      className={dayClass}
                      onClick={current ? () => handleDayClick(dt) : undefined}
                      onMouseEnter={current ? () => handleDayHover(dt) : undefined}
                      onMouseLeave={current ? handleDayLeave : undefined}
                    >
                      {d}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="dfc-shortcuts">
        <span className="dfc-shortcut" onClick={onClear}>
          Clear
        </span>
        <span className="dfc-shortcut" onClick={goToday}>
          Today
        </span>
      </div>
    </div>
  );
};
